import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'
import Tracker from './Tracker'

const SCHRITTE = [
  { nr: 1, name: 'Übernahme' },
  { nr: 2, name: 'Ersatzteile' },
  { nr: 3, name: 'Demontage' },
  { nr: 4, name: 'Lackvorbereitung' },
  { nr: 5, name: 'Lackierung' },
  { nr: 6, name: 'Lackkontrolle' },
  { nr: 7, name: 'Montage' },
  { nr: 8, name: 'Politur' },
  { nr: 9, name: 'Aufbereitung' },
  { nr: 10, name: 'Übergabe' },
]

const CSS = `
@keyframes flashBorder   { 0%,100%{box-shadow:inset 4px 0 0 #ef4444} 50%{box-shadow:inset 4px 0 0 #ff8080} }
@keyframes fastcarsPulse { 0%,100%{box-shadow:inset 4px 0 0 #7c3aed} 50%{box-shadow:inset 4px 0 0 #a78bfa} }
@keyframes morgenPulse   { 0%,100%{box-shadow:inset 4px 0 0 #f59e0b} 50%{box-shadow:inset 4px 0 0 #fcd34d} }
@keyframes dotPulse      { 0%,100%{box-shadow:0 0 0 0 rgba(255,192,0,0.6)} 50%{box-shadow:0 0 0 5px rgba(255,192,0,0)} }
@keyframes alertSlide    { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
`

function getDringlichkeit(f) {
  if (f.fastcars) return 'fastcars'
  if (!f.wunschdatum) return 'normal'
  const diff = Math.floor((new Date(f.wunschdatum) - new Date()) / 86400000)
  if (diff < 0)  return 'overdue'
  if (diff === 0) return 'critical'
  if (diff === 1) return 'morgen'
  if (diff <= 3)  return 'warning'
  return 'ok'
}

function rowBg(d) {
  if (d === 'overdue')  return { background: '#fff5f5', boxShadow: 'inset 4px 0 0 #c53030', animation: 'flashBorder 1.5s infinite' }
  if (d === 'critical') return { background: '#fff5f5', boxShadow: 'inset 4px 0 0 #ef4444', animation: 'flashBorder 0.8s infinite' }
  if (d === 'morgen')   return { background: '#fffbeb', boxShadow: 'inset 4px 0 0 #f59e0b', animation: 'morgenPulse 1.8s infinite' }
  if (d === 'warning')  return { background: '#fffbeb', boxShadow: 'inset 4px 0 0 #f59e0b' }
  if (d === 'fastcars') return { background: '#faf5ff', boxShadow: 'inset 4px 0 0 #7c3aed', animation: 'fastcarsPulse 2s infinite' }
  if (d === 'ok')       return { background: '#f0fff4', boxShadow: 'inset 4px 0 0 #22c55e' }
  return { background: '#fff' }
}

function DeadlineBadge({ wunschdatum }) {
  if (!wunschdatum) return null
  const diff = Math.floor((new Date(wunschdatum) - new Date()) / 86400000)
  const datum = new Date(wunschdatum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
  if (diff < 0)   return <span style={bs('#c53030')}>⚠ {Math.abs(diff)}T ÜBER</span>
  if (diff === 0)  return <span style={bs('#ef4444')}>🔴 HEUTE</span>
  if (diff === 1)  return <span style={bs('#f59e0b')}>⏰ MORGEN</span>
  if (diff <= 3)   return <span style={bs('#f59e0b')}>{diff}T · {datum}</span>
  return <span style={bs('#e2e8f0', '#4a5568')}>{datum}</span>
}

export default function Board({ userRole, mitarbeiter, isMobile }) {
  const [fahrzeuge, setFahrzeuge]   = useState([])
  const [leihwagenMap, setLeihwagenMap] = useState({})
  const [selected, setSelected]     = useState(null)
  const [filter, setFilter]         = useState('')
  const [deleting, setDeleting]     = useState(null)
  const [confirmF, setConfirmF]     = useState(null)

  useEffect(() => {
    loadFahrzeuge()
    loadLeihwagen()
    const channel = supabase
      .channel('board-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'fahrzeuge' }, () => loadFahrzeuge())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [])

  const loadFahrzeuge = async () => {
    const { data } = await supabase
      .from('fahrzeuge').select('*').is('übergabe_ok', null)
      .order('wunschdatum', { ascending: true, nullsFirst: false })
    setFahrzeuge(data || [])
  }

  const loadLeihwagen = async () => {
    const { data } = await supabase.from('leihwagen').select('id, kennzeichen, modell')
    const map = {}
    ;(data || []).forEach(lw => { map[lw.id] = lw })
    setLeihwagenMap(map)
  }

  const deleteFahrzeug = async (f) => {
    setDeleting(f.id)
    if (f.leihwagen_id) {
      await supabase.from('leihwagen').update({ verfügbar: true, vergeben_an: null }).eq('id', f.leihwagen_id)
    }
    await supabase.from('fahrzeuge').delete().eq('id', f.id)
    setDeleting(null)
    loadFahrzeuge()
  }

  const filtered = fahrzeuge.filter(f => {
    if (!filter) return true
    const q = filter.toLowerCase()
    return (
      f.kennzeichen?.toLowerCase().includes(q) ||
      f.kunde?.toLowerCase().includes(q) ||
      f.marke?.toLowerCase().includes(q) ||
      f.modell?.toLowerCase().includes(q)
    )
  })

  const overdue   = filtered.filter(f => f.wunschdatum && new Date(f.wunschdatum) < new Date())
  const morgen    = filtered.filter(f => { if (!f.wunschdatum) return false; return Math.floor((new Date(f.wunschdatum) - new Date()) / 86400000) === 1 })
  const kritisch  = filtered.filter(f => { if (!f.wunschdatum) return false; const d = Math.floor((new Date(f.wunschdatum) - new Date()) / 86400000); return d === 0 })
  const fastcarsAktiv = filtered.filter(f => f.fastcars)

  return (
    <div style={{ padding: '16px 20px' }}>
      <style>{CSS}</style>

      {/* ── Alert Banners ── */}
      {overdue.length > 0 && (
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 16px', marginBottom: 8, animation: 'alertSlide 0.3s ease', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#be123c' }}>🚨 Überfällig:</span>
          {overdue.map(f => (
            <span key={f.id} style={{ background: '#be123c', color: '#fff', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }} onClick={() => setSelected(f)}>
              {f.kennzeichen} · {Math.abs(Math.floor((new Date(f.wunschdatum) - new Date()) / 86400000))}T
            </span>
          ))}
        </div>
      )}

      {(morgen.length > 0 || kritisch.length > 0) && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 16px', marginBottom: 8, animation: 'alertSlide 0.3s ease', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>⏰ Bald fällig:</span>
          {kritisch.map(f => (
            <span key={f.id} style={{ background: '#ef4444', color: '#fff', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }} onClick={() => setSelected(f)}>
              {f.kennzeichen} · HEUTE
            </span>
          ))}
          {morgen.map(f => (
            <span key={f.id} style={{ background: '#f59e0b', color: '#fff', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }} onClick={() => setSelected(f)}>
              {f.kennzeichen} · MORGEN
            </span>
          ))}
        </div>
      )}

      {/* ── Stats + Search ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
        {overdue.length > 0    && <span style={badge('#c53030', '#fff')}>⚠ {overdue.length} Überfällig</span>}
        {kritisch.length > 0   && <span style={badge('#ef4444', '#fff')}>🔴 {kritisch.length} Heute</span>}
        {morgen.length > 0     && <span style={badge('#f59e0b', '#fff')}>⏰ {morgen.length} Morgen</span>}
        {fastcarsAktiv.length > 0 && <span style={badge('#7c3aed', '#fff')}>🔧 {fastcarsAktiv.length} FastCars</span>}
        <span style={badge('#e2e8f0', '#4a5568')}>{filtered.length} Gesamt</span>
        <input
          style={{ padding: '7px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: 200 }}
          placeholder="Suchen..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>

      {/* ── Table ── */}
      <div style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', overflowX: 'auto' }}>
       <div style={{ minWidth: isMobile ? 860 : 0 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', background: C.dark, padding: '10px 14px' }}>
          <div style={colKfz}><span style={hdr}>Fahrzeug</span></div>
          <div style={colTermin}><span style={hdr}>Termin</span></div>
          <div style={colTage}><span style={hdr}>Tage</span></div>
          {SCHRITTE.map(s => (
            <div key={s.nr} style={colStep}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.primary }}>{s.nr}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{s.name}</div>
            </div>
          ))}
          <div style={{ width: 32, flexShrink: 0 }} />
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: '#999', background: '#fff' }}>Keine aktiven Fahrzeuge</div>
        )}

        {filtered.map(f => {
          const d    = getDringlichkeit(f)
          const tage = Math.floor((new Date() - new Date(f.eingang)) / 86400000)
          const lw   = f.leihwagen_id ? leihwagenMap[f.leihwagen_id] : null
          const isOverdue = f.wunschdatum && new Date(f.wunschdatum) < new Date()

          return (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.15s', ...rowBg(d) }}
              onClick={() => setSelected(f)}>

              {/* Fahrzeug */}
              <div style={colKfz}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{f.kennzeichen}</div>
                <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.marke} {f.modell}</div>
                <div style={{ display: 'flex', gap: 3, marginTop: 2, flexWrap: 'wrap' }}>
                  {f.fastcars && <span style={tag('#7c3aed')}>FC</span>}
                  {f.problem  && <span style={tag('#ef4444')}>!</span>}
                  {lw && (
                    <span style={tag(isOverdue ? '#dc2626' : '#0369a1')}>
                      🚗 {lw.kennzeichen}
                    </span>
                  )}
                </div>
              </div>

              {/* Termin */}
              <div style={colTermin}><DeadlineBadge wunschdatum={f.wunschdatum} /></div>

              {/* Tage */}
              <div style={{ ...colTage, fontWeight: 700, fontSize: 13, color: tage > 7 ? '#c53030' : tage > 4 ? '#d69e2e' : '#276749' }}>{tage}d</div>

              {/* Steps */}
              {SCHRITTE.map(s => {
                const done   = f.schritt > s.nr
                const active = f.schritt === s.nr
                return (
                  <div key={s.nr} style={colStep}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', margin: '0 auto',
                      background: active ? C.primary : done ? '#22c55e' : 'transparent',
                      border: active || done ? 'none' : '2px solid #d1d5db',
                      animation: active ? 'dotPulse 1.2s infinite' : 'none',
                    }} />
                  </div>
                )
              })}

              {/* Delete */}
              <div style={{ width: 32, flexShrink: 0, textAlign: 'center' }}>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 15, padding: 4, borderRadius: 4, lineHeight: 1 }}
                  title="Fahrzeug löschen"
                  onClick={e => { e.stopPropagation(); setConfirmF(f) }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                  disabled={deleting === f.id}
                >
                  {deleting === f.id ? '…' : '✕'}
                </button>
              </div>
            </div>
          )
        })}
       </div>
      </div>

      {selected && (
        <Tracker fahrzeug={selected} onClose={() => { setSelected(null); loadFahrzeuge() }} userRole={userRole} mitarbeiter={mitarbeiter} isMobile={isMobile} />
      )}

      {confirmF && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗑</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 6 }}>Fahrzeug löschen?</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
              <strong style={{ color: '#0d1b2a' }}>{confirmF.kennzeichen}</strong> – {confirmF.marke} {confirmF.modell}
            </div>
            {confirmF.leihwagen_id && (
              <div style={{ fontSize: 12, color: '#d97706', marginBottom: 4 }}>⚠ Leihwagen wird automatisch freigegeben</div>
            )}
            <div style={{ fontSize: 12, color: '#999', marginBottom: 20 }}>Diese Aktion kann nicht rückgängig gemacht werden.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                style={{ padding: '10px 24px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                onClick={() => setConfirmF(null)}>
                Abbrechen
              </button>
              <button
                style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
                disabled={deleting === confirmF.id}
                onClick={() => { deleteFahrzeug(confirmF); setConfirmF(null) }}>
                {deleting === confirmF.id ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const badge  = (bg, color) => ({ background: bg, color, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700 })
const bs     = (bg, color = '#fff') => ({ background: bg, color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 })
const tag    = (bg) => ({ background: bg, color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 9, fontWeight: 700 })
const hdr    = { fontSize: 11, fontWeight: 700, color: '#9ca3af' }
const colKfz    = { width: 175, flexShrink: 0, paddingRight: 8 }
const colTermin = { width: 96,  flexShrink: 0, paddingRight: 4 }
const colTage   = { width: 48,  flexShrink: 0, textAlign: 'center' }
const colStep   = { flex: 1, textAlign: 'center', minWidth: 36 }
